import {Card, Action} from 'platform/components';
import {VStack} from 'platform/foundation';

import {useState} from 'react';

import {CustomerResponseBodyV2} from '@dms/api/customer';
import {PaymentTypeResponseBody} from '@dms/api/finance';
import {BaseAuthorizationProfile} from '@dms/api/security';
import {CurrencyResponseBody} from '@dms/api/shared';
import {GetReceiveNoteCorrectionResponse, GetWarehousesResponse} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {usePermissions} from '@dms/shared';

import {buildArray, Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CorrectionCard} from './CorrectionCard';
import {OriginalCard} from './OriginalCard';

interface GeneralInformationTabProps extends RequiredTestIdProps {
  receiveNoteCorrection: GetReceiveNoteCorrectionResponse | Nullish;
  suppliers: CustomerResponseBodyV2[] | Nullish;
  warehouses: GetWarehousesResponse | Nullish;
  authorizationProfiles: BaseAuthorizationProfile[] | Nullish;
  paymentTypes: PaymentTypeResponseBody[] | Nullish;
  currencies: CurrencyResponseBody[] | Nullish;
}

export function GeneralInformationTab(props: GeneralInformationTabProps) {
  const [isReadOnly, setIsReadOnly] = useState<boolean>(true);

  const [receiveNoteCorrectionEdit, receiveNoteCorrectionEditCompleted] = usePermissions({
    permissionKeys: ['receiveNoteCorrectionEdit', 'receiveNoteCorrectionEditCompleted'],
  });

  const isReceiveNoteCorrectionCompleted =
    props.receiveNoteCorrection?.creditNote?.state === 'COMPLETED';

  // A pending correction is edited with EDIT, a completed one with EDIT_COMPLETED.
  const canEditReceiveNoteCorrection = isReceiveNoteCorrectionCompleted
    ? receiveNoteCorrectionEditCompleted
    : receiveNoteCorrectionEdit;

  const receiveNoteActions = buildArray<Action>().when(isReadOnly, {
    type: 'button',
    variant: 'link',
    leftIcon: 'image/edit',
    title: i18n.t('general.labels.edit'),
    isDisabled: !canEditReceiveNoteCorrection,
    tooltip: !canEditReceiveNoteCorrection
      ? i18n.t('module.warehouse.label.missingReceiveNoteCorrectionEditPermission')
      : undefined,
    onClick: () => setIsReadOnly(false),
  });

  return (
    <VStack spacing={4}>
      <Card
        flags={[{label: i18n.t('entity.warehouse.labels.correction'), colorScheme: 'red'}]}
        actions={receiveNoteActions}
      >
        <CorrectionCard
          receiveNoteCorrection={props.receiveNoteCorrection}
          suppliers={props.suppliers}
          warehouses={props.warehouses}
          authorizationProfiles={props.authorizationProfiles}
          paymentTypes={props.paymentTypes}
          currencies={props.currencies}
          isReadOnly={isReadOnly}
          onClose={() => setIsReadOnly(true)}
          data-testid={suffixTestId('cards.correction', props)}
        />
      </Card>
      <Card flags={[{label: i18n.t('entity.warehouse.labels.original'), colorScheme: 'black'}]}>
        <OriginalCard
          receiveNoteCorrection={props.receiveNoteCorrection}
          suppliers={props.suppliers}
          warehouses={props.warehouses}
          authorizationProfiles={props.authorizationProfiles}
          paymentTypes={props.paymentTypes}
          currencies={props.currencies}
          data-testid={suffixTestId('cards.original', props)}
        />
      </Card>
    </VStack>
  );
}
